import { useId } from 'react';

/**
 * Controlled number input с unit suffix для размерных полей анкет калькулятора.
 * Block 12 PR-B Phase B1.
 *
 * Используется для длины, высоты, глубины, количества (мм / м² / ₸ / шт).
 * Структура копирует `TextInput.tsx` — label / input / hint-or-error pattern,
 * P-022 a11y baseline (htmlFor, aria-invalid, aria-describedby, focus-visible,
 * min-h-tap).
 *
 * Контракт onChange:
 * - Empty string → `onChange(null)` (clearable).
 * - Valid finite number → `onChange(parseFloat(value))`.
 * - Невалидный ввод (NaN) → нет onChange (защита от мусора).
 *
 * Visual states:
 * - default: `border-accent`.
 * - aria-invalid="true": `border-error` (триггерится либо `error` prop, либо
 *   client-side range check value < min / value > max).
 * - disabled: `bg-icon-bg text-muted cursor-not-allowed`.
 *
 * Unit suffix (если передан): рендерится `<span aria-hidden>` в правой части
 * поля поверх input, чтобы не мешать parsing'у value (input.value остаётся
 * чистым числом). pr-12 на input резервирует место под suffix.
 *
 * min/max ограничения — обязательно через aria-invalid (не доверяем browser
 * default UI), чтобы screen reader проговорил «invalid» и наш visual border-error
 * сработал.
 */
export interface NumberInputProps {
  name: string;
  label: string;
  /** Текущее значение (null = пусто). */
  value: number | null;
  onChange: (value: number | null) => void;
  /** Suffix справа от input (например 'мм', 'м²', '₸', 'шт'). */
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  /** Подсказка под полем (скрывается при error). */
  hint?: string;
  /** Сообщение об ошибке — overrides hint, role=alert. */
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

export function NumberInput({
  name,
  label,
  value,
  onChange,
  unit,
  min,
  max,
  step,
  hint,
  error,
  required,
  disabled,
  placeholder,
}: NumberInputProps) {
  const reactId = useId();
  const inputId = `${name}-${reactId}`;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;

  const describedBy = error ? errorId : hint ? hintId : undefined;
  const outOfRange =
    value !== null && ((min !== undefined && value < min) || (max !== undefined && value > max));
  const hasError = Boolean(error) || outOfRange;

  const displayValue = value === null ? '' : String(value);

  const handleChange = (raw: string) => {
    if (raw === '') {
      onChange(null);
      return;
    }
    const parsed = parseFloat(raw);
    if (Number.isFinite(parsed)) {
      onChange(parsed);
    }
    // else: silently ignore (browser type=number обычно сам фильтрует, но защищаемся).
  };

  return (
    <div className="flex flex-col">
      <label htmlFor={inputId} className="text-ink font-semibold text-base mb-2">
        {label}
        {required ? (
          <span aria-hidden="true" className="text-error ml-1">
            *
          </span>
        ) : null}
      </label>
      <div className="relative">
        <input
          id={inputId}
          name={name}
          type="number"
          inputMode="decimal"
          value={displayValue}
          onChange={(e) => handleChange(e.target.value)}
          min={min}
          max={max}
          step={step}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          aria-invalid={hasError || undefined}
          aria-describedby={describedBy}
          className={
            'w-full min-h-tap rounded-xl bg-card text-ink text-base px-3 py-3 ' +
            (unit ? 'pr-12 ' : '') +
            'border-[1.5px] ' +
            (hasError ? 'border-error ' : 'border-accent ') +
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none ' +
            'disabled:bg-icon-bg disabled:text-muted disabled:cursor-not-allowed'
          }
        />
        {unit ? (
          <span
            aria-hidden="true"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted text-sm pointer-events-none"
          >
            {unit}
          </span>
        ) : null}
      </div>
      {error ? (
        <p id={errorId} role="alert" className="text-error text-sm mt-1">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-muted text-sm mt-1">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
